import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Download, LogOut, RefreshCw, RotateCcw, Send, UploadCloud } from "lucide-react";
import { useAuth, authFetch } from "../auth";
import { useLang } from "../i18n";
import AuthImage from "../components/AuthImage";

const STATUS_META = [
  ["brief", { en: "Brief", it: "Brief" }],
  ["cad", { en: "CAD Design", it: "Design CAD" }],
  ["prototype", { en: "Prototype", it: "Prototipo" }],
  ["casting", { en: "Casting", it: "Fusione" }],
  ["finishing", { en: "Finishing", it: "Finitura" }],
  ["delivered", { en: "Delivered", it: "Consegnato" }],
];

const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export default function Portal() {
  const { client, logout } = useAuth();
  const { L } = useLang();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [msg, setMsg] = useState("");
  const [recastNote, setRecastNote] = useState("");
  const [showRecast, setShowRecast] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (client === null) navigate("/login");
  }, [client, navigate]);

  const loadProjects = useCallback(async () => {
    const r = await authFetch("/portal/projects");
    if (r.ok) {
      const data = await r.json();
      setProjects(data);
      if (data.length && !selected) setSelected(data[0].id);
    }
  }, [selected]);

  const loadDetail = useCallback(async (pid) => {
    if (!pid) return;
    const r = await authFetch(`/portal/projects/${pid}`);
    if (r.ok) setDetail(await r.json());
  }, []);

  useEffect(() => {
    if (client) loadProjects();
  }, [client, loadProjects]);

  useEffect(() => {
    if (selected) loadDetail(selected);
  }, [selected, loadDetail]);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const deposit = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    const pid = searchParams.get("project");
    if (!deposit || !client) return;
    setSearchParams({}, { replace: true });
    if (pid) setSelected(pid);
    if (deposit === "success" && sessionId && pid) {
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const r = await authFetch(`/portal/payments/status/${sessionId}`);
          const d = await r.json();
          if (d.payment_status === "paid") {
            clearInterval(poll);
            toast.success(L({ en: "Deposit received — thank you! Your production slot is confirmed.", it: "Acconto ricevuto — grazie! Il tuo posto in produzione è confermato." }));
            loadProjects();
            loadDetail(pid);
          } else if (tries > 8) {
            clearInterval(poll);
            toast.info(L({ en: "Payment is being confirmed — it will appear shortly.", it: "Pagamento in conferma — apparirà a breve." }));
          }
        } catch { /* keep polling */ }
      }, 2000);
      return () => clearInterval(poll);
    }
    if (deposit === "cancelled") {
      toast.info(L({ en: "Payment cancelled — you can pay the deposit whenever you're ready.", it: "Pagamento annullato — puoi pagare l'acconto quando vorrai." }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  if (client === undefined) {
    return <div className="flex min-h-[60vh] items-center justify-center text-[#8A8F82]" data-testid="portal-loading">···</div>;
  }
  if (!client) return null;

  const project = detail?.project;
  const statusIdx = project ? STATUS_META.findIndex(([k]) => k === project.status) : 0;

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await authFetch(`/portal/projects/${selected}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: msg }),
      });
      setMsg("");
      await loadDetail(selected);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await authFetch(`/portal/projects/${selected}/files`, { method: "POST", body: fd });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "upload failed");
      }
      toast.success(L({ en: "File uploaded.", it: "File caricato." }));
      await loadDetail(selected);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (f) => {
    const r = await authFetch(`/portal/files/${f.id}/download`);
    if (!r.ok) return toast.error(L({ en: "Download failed.", it: "Download non riuscito." }));
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = f.original_filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const payPayment = async (kind) => {
    setBusy(true);
    try {
      const r = await authFetch(`/portal/projects/${selected}/pay/${kind}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "checkout failed");
      window.location.href = d.checkout_url;
    } catch (err) {
      toast.error(err.message);
      setBusy(false);
    }
  };

  const downloadReceipt = async (sessionId) => {
    const r = await authFetch(`/portal/receipts/${sessionId}/download`);
    if (!r.ok) return toast.error(L({ en: "Receipt unavailable.", it: "Ricevuta non disponibile." }));
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ricevuta-${sessionId.slice(-8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const requestRecast = async () => {
    setBusy(true);
    try {
      await authFetch(`/portal/projects/${selected}/recast`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: recastNote }),
      });
      toast.success(L({ en: "Re-cast request sent to the atelier.", it: "Richiesta di rifusione inviata all'atelier." }));
      setRecastNote("");
      setShowRecast(false);
      await loadProjects();
      await loadDetail(selected);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-14" data-testid="portal-page">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="mono-tech mb-3 text-[#A8853B]">{L({ en: "Client Area", it: "Area Clienti" })}</p>
          <h1 className="font-display text-3xl text-[#0E3B33] sm:text-4xl" data-testid="portal-greeting">
            {L({ en: "Welcome,", it: "Benvenuto," })} {client.name}
          </h1>
          <p className="mt-2 text-sm text-[#8A8F82]">{client.company || client.email}</p>
        </div>
        <button data-testid="portal-logout-btn" onClick={() => { logout(); navigate("/"); }} className="ghost-btn !py-2.5 !px-5 text-sm">
          <LogOut className="h-4 w-4" /> {L({ en: "Sign out", it: "Esci" })}
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="rounded-xl border border-[#0E3B33]/10 bg-white p-12 text-center text-sm text-[#8A8F82]" data-testid="portal-empty">
          {L({ en: "No projects yet — your commissions will appear here once the atelier opens them.", it: "Nessun progetto ancora — le tue commissioni appariranno qui quando l'atelier le aprirà." })}
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-12">
          {/* project list */}
          <div className="space-y-3 lg:col-span-4" data-testid="portal-project-list">
            {projects.map((p) => (
              <button
                key={p.id}
                data-testid={`project-card-${p.id}`}
                onClick={() => setSelected(p.id)}
                className={`w-full rounded-lg border bg-white p-5 text-left transition-all duration-300 ${
                  selected === p.id ? "border-[#C9A84C] shadow-md" : "border-[#0E3B33]/10 hover:border-[#C9A84C]/50"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-display text-lg text-[#0E3B33]">{p.title}</span>
                  <span className={`mono-tech shrink-0 rounded-full px-2.5 py-1 ${p.status === "recast_requested" ? "bg-amber-100 text-amber-800" : "bg-[#0E3B33]/5 text-[#0E3B33]"}`}>
                    {p.status === "recast_requested" ? L({ en: "Re-cast requested", it: "Rifusione richiesta" }) : L(STATUS_META.find(([k]) => k === p.status)?.[1] || { en: p.status, it: p.status })}
                  </span>
                </div>
                {p.metal && <div className="mt-1.5 text-xs text-[#8A8F82]">{p.metal}</div>}
                <div className="mono-tech mt-3 text-[#8A8F82]">{L({ en: "Updated", it: "Aggiornato" })} {fmtDate(p.updated_at)}</div>
              </button>
            ))}
          </div>

          {/* project detail */}
          {project && (
            <motion.div key={project.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-8 lg:col-span-8" data-testid="portal-project-detail">
              {/* status stepper */}
              <div className="rounded-xl border border-[#0E3B33]/10 bg-white p-6">
                <div className="mono-tech mb-5 text-[#8A8F82]">{L({ en: "Project status", it: "Stato del progetto" })}</div>
                {project.status === "recast_requested" ? (
                  <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="recast-banner">
                    {L({ en: "Re-cast / re-print requested — the atelier will confirm the new run shortly.", it: "Rifusione / ristampa richiesta — l'atelier confermerà a breve la nuova tiratura." })}
                  </div>
                ) : (
                  <div className="flex items-center" data-testid="status-stepper">
                    {STATUS_META.map(([key, label], i) => (
                      <div key={key} className="flex flex-1 items-center last:flex-none">
                        <div className="flex flex-col items-center gap-2">
                          <div className={`flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-semibold ${
                            i <= statusIdx ? "border-[#0E3B33] bg-[#0E3B33] text-[#FBF9F3]" : "border-[#0E3B33]/20 text-[#8A8F82]"
                          }`} data-testid={`status-step-${key}`}>
                            {i + 1}
                          </div>
                          <span className={`mono-tech hidden sm:block ${i <= statusIdx ? "text-[#0E3B33]" : "text-[#8A8F82]"}`}>{L(label)}</span>
                        </div>
                        {i < STATUS_META.length - 1 && <div className={`mx-1 h-px flex-1 ${i < statusIdx ? "bg-[#0E3B33]" : "bg-[#0E3B33]/15"}`} />}
                      </div>
                    ))}
                  </div>
                )}
                {project.status === "delivered" && !showRecast && (
                  <button data-testid="recast-open-btn" onClick={() => setShowRecast(true)} className="ghost-btn mt-6 !py-2.5 !px-5 text-sm">
                    <RotateCcw className="h-4 w-4" /> {L({ en: "Request re-cast / re-print", it: "Richiedi rifusione / ristampa" })}
                  </button>
                )}
                {showRecast && project.status === "delivered" && (
                  <div className="mt-6 space-y-3">
                    <textarea
                      rows={2}
                      data-testid="recast-note-input"
                      className="forge-field resize-none"
                      placeholder={L({ en: "Quantity, changes, or notes for the new run…", it: "Quantità, modifiche o note per la nuova tiratura…" })}
                      value={recastNote}
                      onChange={(e) => setRecastNote(e.target.value)}
                    />
                    <div className="flex gap-3">
                      <button data-testid="recast-submit-btn" disabled={busy} onClick={requestRecast} className="ember-btn !py-2.5 !px-5 text-sm disabled:opacity-60">
                        {L({ en: "Confirm request", it: "Conferma richiesta" })}
                      </button>
                      <button data-testid="recast-cancel-btn" onClick={() => setShowRecast(false)} className="ghost-btn !py-2.5 !px-5 text-sm">
                        {L({ en: "Cancel", it: "Annulla" })}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* payments */}
              {["deposit", "balance"]
                .filter((k) => (project[`${k}_amount`] || 0) > 0)
                .map((k) => {
                  const paid = project[`${k}_status`] === "paid";
                  const blocked = k === "balance" && (project.deposit_amount || 0) > 0 && project.deposit_status !== "paid";
                  const label = k === "deposit" ? L({ en: "Deposit", it: "Acconto" }) : L({ en: "Final balance", it: "Saldo finale" });
                  const payLabel = k === "deposit" ? L({ en: "Pay deposit", it: "Paga l'acconto" }) : L({ en: "Pay balance", it: "Paga il saldo" });
                  return (
                    <div key={k} className="rounded-xl border border-[#C9A84C]/50 bg-white p-6" data-testid={`${k}-card`}>
                      {paid ? (
                        <div className="flex items-center gap-3" data-testid={`${k}-paid-banner`}>
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                            <CheckCircle2 className="h-5 w-5 text-emerald-700" />
                          </span>
                          <div>
                            <div className="text-sm font-medium text-emerald-800">{label} — {L({ en: "paid, thank you", it: "pagato, grazie" })}</div>
                            <div className="mono-tech text-[#8A8F82]">
                              €{Number(project[`${k}_amount`]).toFixed(2)}{project[`${k}_paid_at`] ? ` · ${fmtDate(project[`${k}_paid_at`])}` : ""}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center justify-between gap-5">
                          <div>
                            <div className="mono-tech text-[#A8853B]">{label}</div>
                            <div className="mt-1 font-display text-3xl text-[#0E3B33]" data-testid={`${k}-amount`}>€{Number(project[`${k}_amount`]).toFixed(2)}</div>
                            <p className="mt-2 max-w-sm text-xs leading-relaxed text-[#8A8F82]">
                              {blocked
                                ? L({ en: "Available once the deposit is paid.", it: "Disponibile una volta pagato l'acconto." })
                                : k === "deposit"
                                  ? L({ en: "The deposit secures your place in the production schedule. Secure card payment via Stripe — you'll return here automatically.", it: "L'acconto conferma il tuo posto nel calendario di produzione. Pagamento sicuro con carta via Stripe — tornerai qui automaticamente." })
                                  : L({ en: "The final balance completes your commission before shipping. Secure card payment via Stripe.", it: "Il saldo finale completa la tua commissione prima della spedizione. Pagamento sicuro con carta via Stripe." })}
                            </p>
                          </div>
                          <button data-testid={`${k}-pay-btn`} disabled={busy || blocked} onClick={() => payPayment(k)} className="ember-btn disabled:opacity-50">
                            {payLabel} <ArrowRight className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

              {/* payment history */}
              {(detail.payments || []).length > 0 && (
                <div className="rounded-xl border border-[#0E3B33]/10 bg-white p-6" data-testid="payment-history">
                  <div className="mono-tech mb-5 text-[#8A8F82]">{L({ en: "Payment history", it: "Storico pagamenti" })}</div>
                  <div className="space-y-2">
                    {detail.payments.map((p) => (
                      <div key={p.session_id} className="flex items-center justify-between rounded-md border border-[#0E3B33]/10 px-4 py-3" data-testid={`receipt-row-${p.session_id.slice(-8)}`}>
                        <div className="min-w-0">
                          <div className="text-sm text-[#1E2B26]">
                            {(p.kind === "deposit" ? L({ en: "Deposit", it: "Acconto" }) : L({ en: "Final balance", it: "Saldo finale" }))} — €{(p.amount / 100).toFixed(2)}
                          </div>
                          <div className="mono-tech text-[#8A8F82]">{fmtDate(p.updated_at)} · ref {p.session_id.slice(-8).toUpperCase()}</div>
                        </div>
                        <button
                          data-testid={`receipt-download-${p.session_id.slice(-8)}`}
                          onClick={() => downloadReceipt(p.session_id)}
                          className="ml-4 flex shrink-0 items-center gap-2 text-sm font-medium text-[#A8853B] transition-colors hover:text-[#0E3B33]"
                        >
                          <Download className="h-4 w-4" /> {L({ en: "Receipt", it: "Ricevuta" })}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* documents */}
              <div className="rounded-xl border border-[#0E3B33]/10 bg-white p-6" data-testid="portal-documents">
                <div className="mono-tech mb-5 text-[#8A8F82]">{L({ en: "Documents & files", it: "Documenti e file" })}</div>
                <label className="block cursor-pointer border border-dashed border-[#0E3B33]/20 p-6 text-center transition-colors hover:border-[#C9A84C]" data-testid="portal-upload-zone">
                  <UploadCloud className="mx-auto mb-2 h-5 w-5 text-[#A8853B]" strokeWidth={1.5} />
                  <p className="text-sm text-[#4A5248]">{L({ en: "Upload sketches, references or CAD files", it: "Carica bozzetti, riferimenti o file CAD" })}</p>
                  <p className="mt-1 text-[11px] text-[#8A8F82]">JPG, PNG, PDF, DXF, STL, OBJ, 3DM, ZIP — max 10MB</p>
                  <input ref={fileRef} type="file" className="hidden" data-testid="portal-file-input" accept=".jpg,.jpeg,.png,.pdf,.dxf,.stl,.obj,.3dm,.zip" onChange={upload} />
                </label>
                <div className="mt-4 space-y-2">
                  {(detail.files || []).map((f) => (
                    <div key={f.id} className="flex items-center justify-between rounded-md border border-[#0E3B33]/10 px-4 py-3" data-testid={`file-row-${f.id}`}>
                      <div className="min-w-0">
                        <div className="truncate text-sm text-[#1E2B26]">{f.original_filename}</div>
                        <div className="mono-tech text-[#8A8F82]">
                          {f.uploaded_by === "team" ? L({ en: "Shared by atelier", it: "Dall'atelier" }) : L({ en: "Uploaded by you", it: "Caricato da te" })} · {fmtSize(f.size)} · {fmtDate(f.created_at)}
                        </div>
                      </div>
                      <button data-testid={`file-download-${f.id}`} onClick={() => download(f)} className="ml-4 flex shrink-0 items-center gap-2 text-sm font-medium text-[#A8853B] transition-colors hover:text-[#0E3B33]">
                        <Download className="h-4 w-4" /> {L({ en: "Download", it: "Scarica" })}
                      </button>
                    </div>
                  ))}
                  {(!detail.files || detail.files.length === 0) && (
                    <p className="text-xs text-[#8A8F82]">{L({ en: "No files yet — CAD files shared by the atelier will appear here too.", it: "Nessun file ancora — anche i file CAD condivisi dall'atelier appariranno qui." })}</p>
                  )}
                </div>
              </div>

              {/* message thread */}
              <div className="rounded-xl border border-[#0E3B33]/10 bg-white p-6" data-testid="portal-thread">
                <div className="mb-5 flex items-center justify-between">
                  <span className="mono-tech text-[#8A8F82]">{L({ en: "Conversation with the atelier", it: "Conversazione con l'atelier" })}</span>
                  <button data-testid="thread-refresh-btn" onClick={() => loadDetail(selected)} className="text-[#8A8F82] transition-colors hover:text-[#0E3B33]">
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
                <div className="max-h-80 space-y-4 overflow-y-auto pr-1">
                  {(detail.messages || []).map((m) => (
                    <div key={m.id} className={`flex ${m.author === "client" ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                      <div className={`max-w-[80%] rounded-lg px-4 py-3 text-sm leading-relaxed ${
                        m.author === "client" ? "bg-[#0E3B33] text-[#FBF9F3]" : m.author === "system" ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-[#F2EDE3] text-[#1E2B26]"
                      }`}>
                        {m.author !== "client" && (
                          <div className="mono-tech mb-1 opacity-70">{m.author === "team" ? "DA Creations" : L({ en: "System", it: "Sistema" })}</div>
                        )}
                        {m.file_id && (detail.files || []).find((f) => f.id === m.file_id)?.content_type?.startsWith("image/") && (
                          <AuthImage fileId={m.file_id} alt={m.body} className="mb-2 max-h-52 rounded-md object-cover" />
                        )}
                        {m.body}
                        <div className="mono-tech mt-1.5 opacity-50">{fmtDate(m.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <form onSubmit={sendMessage} className="mt-5 flex gap-3">
                  <input
                    data-testid="thread-message-input"
                    className="forge-field"
                    placeholder={L({ en: "Ask for a change, share an update…", it: "Chiedi una modifica, condividi un aggiornamento…" })}
                    value={msg}
                    onChange={(e) => setMsg(e.target.value)}
                  />
                  <button type="submit" disabled={busy || !msg.trim()} data-testid="thread-send-btn" className="ember-btn shrink-0 !px-5 disabled:opacity-60">
                    <Send className="h-4 w-4" />
                  </button>
                </form>
              </div>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
}
